// Seitenleiste der Schadenmappe (Spec §1): Oberpunkte Meldung, Bearbeitung
// und Regulierung, unter „Meldung“
// die Schritte der aktuellen Schadenart mit ihrem Zustand. Auf dem Telefon
// wird daraus eine aufklappbare Zeile, die den aktuellen Punkt nennt.
import * as React from "react";
import type { BigStepKey } from "../schadenmeldung-big/types";
import type { Bereich, SchrittEintrag } from "./meldung/schritte";

export interface SeitenleisteProps {
  /** Aus `schrittEintraege()`. */
  schritte: SchrittEintrag[];
  bereich: Bereich;
  schritt: BigStepKey | null;
  /** Vor der Anlage: nur „Kontakt“ (`kontakt`) und „Schadenart“ (`type`) sind aktiv. */
  gesperrt: boolean;
  onWechsel: (bereich: Bereich, schritt?: BigStepKey) => void;
  /** Aufklappbare Kopfvariante für schmale Bildschirme. */
  telefon: boolean;
}

const BEREICH_LABEL: Record<Bereich, string> = {
  meldung: "Meldung",
  bearbeitung: "Bearbeitung",
  regulierung: "Regulierung",
};

const SPERR_HINWEIS = "Erst Kontakt und Schadenart wählen";

const ZEICHEN: Record<SchrittEintrag["zustand"], string> = {
  ok: "✓",
  fehlt: "!",
  aktuell: "●",
  leer: "",
};

const ZUSTAND_TEXT: Record<SchrittEintrag["zustand"], string> = {
  ok: "ausgefüllt",
  fehlt: "Pflichtangabe fehlt",
  aktuell: "aktueller Schritt",
  leer: "noch nicht besucht",
};

interface PunktProps {
  className: string;
  gesperrt: boolean;
  aktuell: "step" | "page" | undefined;
  onClick: () => void;
  children: React.ReactNode;
}

/** Ein anklickbarer Punkt; gesperrt bleibt er fokussierbar (Tooltip), tut aber nichts. */
function Punkt({ className, gesperrt, aktuell, onClick, children }: PunktProps) {
  return (
    <button
      type="button"
      className={className + (gesperrt ? " ist-gesperrt" : "")}
      aria-disabled={gesperrt ? "true" : undefined}
      aria-current={aktuell}
      title={gesperrt ? SPERR_HINWEIS : undefined}
      onClick={gesperrt ? undefined : onClick}
    >
      {children}
    </button>
  );
}

export function Seitenleiste({ schritte, bereich, schritt, gesperrt, onWechsel, telefon }: SeitenleisteProps) {
  const liste = (
    <nav className="smp-leiste-nav" aria-label="Schadenmappe">
      <Punkt
        className={"smp-leiste-ober" + (bereich === "meldung" ? " ist-aktiv" : "")}
        gesperrt={false}
        aktuell={undefined}
        onClick={() => onWechsel("meldung")}
      >
        {BEREICH_LABEL.meldung}
      </Punkt>
      <ol className="smp-leiste-schritte">
        {schritte.map((s) => (
          <li key={s.key}>
            <Punkt
              className={`smp-leiste-schritt ist-${s.zustand}`}
              gesperrt={gesperrt && s.key !== "type" && s.key !== "kontakt"}
              aktuell={bereich === "meldung" && s.key === schritt ? "step" : undefined}
              onClick={() => onWechsel("meldung", s.key)}
            >
              <span className="smp-leiste-zeichen" aria-hidden="true">{ZEICHEN[s.zustand]}</span>
              <span className="smp-leiste-text">{s.label}</span>
              <span className="smp-sr">{` (${ZUSTAND_TEXT[s.zustand]})`}</span>
            </Punkt>
          </li>
        ))}
      </ol>
      <Punkt
        className={"smp-leiste-ober" + (bereich === "bearbeitung" ? " ist-aktiv" : "")}
        gesperrt={gesperrt}
        aktuell={bereich === "bearbeitung" ? "page" : undefined}
        onClick={() => onWechsel("bearbeitung")}
      >
        {BEREICH_LABEL.bearbeitung}
      </Punkt>
      <Punkt
        className={"smp-leiste-ober" + (bereich === "regulierung" ? " ist-aktiv" : "")}
        gesperrt={gesperrt}
        aktuell={bereich === "regulierung" ? "page" : undefined}
        onClick={() => onWechsel("regulierung")}
      >
        {BEREICH_LABEL.regulierung}
      </Punkt>
    </nav>
  );

  if (!telefon) return <aside className="smp-leiste">{liste}</aside>;

  const aktuellerSchritt = bereich === "meldung" ? schritte.find((s) => s.key === schritt) : undefined;
  const zusammenfassung = aktuellerSchritt
    ? `${BEREICH_LABEL.meldung} · ${aktuellerSchritt.label}`
    : BEREICH_LABEL[bereich];
  return (
    <aside className="smp-leiste is-telefon">
      <details className="smp-leiste-auf">
        <summary className="smp-leiste-summary">{zusammenfassung}</summary>
        {liste}
      </details>
    </aside>
  );
}
